import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { AlertTriangle } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { Soft } from "../../Common/Soft";
import { tokens } from "@/theme/tokens";
import { entryVariants, resolveTransition } from "../motion";
import type { PendingConfirmation } from "../../../chat/hooks/useStreamingTurn";

export interface ConfirmationCardProps {
  confirmation: PendingConfirmation;
  onApprove: (summary: string) => void | Promise<void>;
  onCancel: () => void | Promise<void>;
}

/**
 * Inline confirmation for a mutating tool call. The model emits a
 * <confirm> block (bot_confirmation); the user approves/declines by
 * sending a follow-up chat message — there is no dedicated endpoint.
 */
export function ConfirmationCard({ confirmation, onApprove, onCancel }: ConfirmationCardProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reduce = useReducedMotion() ?? false;

  const run = (fn: () => void | Promise<void>) => async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={entryVariants}
      transition={resolveTransition(reduce)}
    >
    <Soft
      variant="raised"
      role="region"
      aria-label="Action confirmation"
      sx={{
        my: 1.5,
        p: 1.5,
        // Warm warning accent on a neumorphic surface: a soft terracotta ring
        // layered over the extrusion signals "needs your call" without the
        // flat tinted fill fighting the soft-UI depth.
        boxShadow: (t) =>
          `${t.palette.mode === "light" ? tokens.neu.light.raised : tokens.neu.dark.raised}, inset 0 0 0 1px ${alpha(t.palette.warning.main, 0.45)}`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5, color: "warning.main" }}>
        <AlertTriangle size={16} aria-hidden />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Confirm action
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ color: "text.primary", mb: 1.25 }}>
        {confirmation.summary || confirmation.action}
      </Typography>
      <Box data-ph-scope="confirmation" sx={{ display: "flex", gap: 1 }}>
        <Button
          size="small"
          variant="contained"
          data-ph="approve"
          disabled={busy}
          onClick={run(() => onApprove(confirmation.summary || confirmation.action))}
        >
          Approve
        </Button>
        <Button size="small" variant="outlined" data-ph="reject" disabled={busy} onClick={run(onCancel)}>
          Cancel
        </Button>
      </Box>
      {error && (
        <Typography variant="caption" sx={{ color: "error.main", display: "block", mt: 0.5 }}>
          {error}
        </Typography>
      )}
    </Soft>
    </motion.div>
  );
}
